import { HttpClient, HttpContext, HttpParams } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { APP_CONFIG } from '@core/config/app-config.tokens';
import {
  HTTP_TIMEOUT_MS,
  SKIP_HTTP_SESSION_AUTH,
  SKIP_HTTP_TIMEOUT,
} from '@core/http/http-context.tokens';
import type {
  ApiHeaders,
  ApiQueryParams,
  ApiRequestOptions,
} from '@core/http/http-request-options';
import { type Observable } from 'rxjs';

interface ApiRequestInit<TBody> extends ApiRequestOptions {
  readonly body?: TBody;
}

interface ApiHttpClientOptions {
  observe: 'body';
  context: HttpContext;
  headers?: ApiHeaders;
  params?: HttpParams;
  reportProgress?: boolean;
}

@Injectable({ providedIn: 'root' })
export class ApiHttpClient {
  private readonly http = inject(HttpClient);
  private readonly appConfig = inject(APP_CONFIG);

  public get<TResponse>(path: string, options?: ApiRequestOptions): Observable<TResponse> {
    return this.http.get<TResponse>(this.buildUrl(path), this.buildOptions(options));
  }

  public post<TResponse, TBody = unknown>(
    path: string,
    body?: TBody,
    options?: ApiRequestOptions,
  ): Observable<TResponse> {
    return this.http.post<TResponse>(this.buildUrl(path), body, this.buildOptions(options));
  }

  public put<TResponse, TBody = unknown>(
    path: string,
    body: TBody,
    options?: ApiRequestOptions,
  ): Observable<TResponse> {
    return this.http.put<TResponse>(this.buildUrl(path), body, this.buildOptions(options));
  }

  public patch<TResponse, TBody = unknown>(
    path: string,
    body: TBody,
    options?: ApiRequestOptions,
  ): Observable<TResponse> {
    return this.http.patch<TResponse>(this.buildUrl(path), body, this.buildOptions(options));
  }

  public delete<TResponse>(path: string, options?: ApiRequestOptions): Observable<TResponse> {
    return this.http.delete<TResponse>(this.buildUrl(path), this.buildOptions(options));
  }

  public request<TResponse, TBody = unknown>(
    method: string,
    path: string,
    options?: ApiRequestInit<TBody>,
  ): Observable<TResponse> {
    const { body, ...requestOptions } = options ?? {};
    const httpOptions = this.buildOptions(requestOptions);

    return this.http.request<TResponse>(
      method,
      this.buildUrl(path),
      body === undefined ? httpOptions : { ...httpOptions, body },
    );
  }

  public buildUrl(path: string): string {
    if (this.isAbsoluteUrl(path)) {
      return path;
    }

    const baseUrl = this.appConfig.apiBaseUrl.replace(/\/+$/, '');
    const normalizedPath = path.replace(/^\/+/, '');

    return normalizedPath.length > 0 ? `${baseUrl}/${normalizedPath}` : baseUrl;
  }

  private buildOptions(options?: ApiRequestOptions): ApiHttpClientOptions {
    const requestOptions: ApiHttpClientOptions = {
      observe: 'body',
      context: this.createContext(options),
    };

    if (options?.headers !== undefined) {
      requestOptions.headers = options.headers;
    }

    if (options?.params !== undefined) {
      requestOptions.params = this.normalizeParams(options.params);
    }

    if (options?.reportProgress !== undefined) {
      requestOptions.reportProgress = options.reportProgress;
    }

    return requestOptions;
  }

  public createContext(options?: ApiRequestOptions): HttpContext {
    const context = options?.context ?? new HttpContext();

    if (options?.timeoutMs !== undefined) {
      context.set(HTTP_TIMEOUT_MS, options.timeoutMs);
    }

    if (options?.skipTimeout) {
      context.set(SKIP_HTTP_TIMEOUT, true);
    }

    if (options?.skipSessionAuth) {
      context.set(SKIP_HTTP_SESSION_AUTH, true);
    }

    return context;
  }

  private normalizeParams(params: HttpParams | ApiQueryParams): HttpParams {
    if (params instanceof HttpParams) {
      return params;
    }

    let httpParams = new HttpParams();

    for (const [key, value] of Object.entries(params)) {
      if (value === null || value === undefined) {
        continue;
      }

      if (Array.isArray(value)) {
        for (const entry of value) {
          httpParams = httpParams.append(key, String(entry));
        }

        continue;
      }

      httpParams = httpParams.set(key, String(value));
    }

    return httpParams;
  }

  private isAbsoluteUrl(path: string): boolean {
    return /^(?:[a-z][a-z\d+.-]*:)?\/\//i.test(path) || /^[a-z][a-z\d+.-]*:/i.test(path);
  }
}
